/**
 * UTM Tracking Utility
 * Captures and manages UTM parameters and referral data for tracking purposes
 */

export interface UTMData {
  landingPage?: string;
  referrer?: string;
  referrerCode?: string; // Explicit referrer code from query parameter
  timestamp?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
}

const UTM_STORAGE_KEY = 'kandir_utm_data';
const REFERRER_CODE_KEY = 'kandirReferrerCode';

/**
 * Capture UTM data from current URL and browser context
 */
export function captureUTMData(): UTMData {
  const searchParameters = new URLSearchParams(window.location.search);
  const utmData = extractUTMParameters(searchParameters);

  // Check for referrerCode query parameter (e.g., ?referrerCode=xyz)
  const referrerCodeParameter = searchParameters.get('referrerCode') || searchParameters.get('ref');
  if (referrerCodeParameter) {
    utmData.referrerCode = referrerCodeParameter;
    // Store in sessionStorage for use across SSO redirects
    try {
      sessionStorage.setItem(REFERRER_CODE_KEY, referrerCodeParameter);
    }
    catch (error) {
      console.warn('[UTM Tracking] Failed to store referrer code:', error);
    }
  }

  // Add additional context
  utmData.referrer = getReferrerInfo();
  utmData.landingPage = window.location.pathname + window.location.search;
  utmData.timestamp = new Date().toISOString();

  return utmData;
}

/**
 * Clear stored UTM data (useful for testing or manual cleanup)
 */
export function clearUTMData(): void {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
    sessionStorage.removeItem(REFERRER_CODE_KEY);
  }
  catch (error) {
    console.warn('[UTM Tracking] Failed to clear UTM data:', error);
  }
}

/**
 * Extract UTM parameters from URL search params
 * @param searchParameters - URLSearchParams object to extract UTM data from
 */
export function extractUTMParameters(searchParameters: URLSearchParams): UTMData {
  const utmData: UTMData = {};

  // Extract UTM parameters
  const utmParameters = [
    'utm_campaign',
    'utm_content',
    'utm_medium',
    'utm_source',
    'utm_term',
  ];

  for (const parameter of utmParameters) {
    const value = searchParameters.get(parameter);
    if (value) {
      // Convert snake_case to camelCase for the interface
      const camelCaseKey = parameter.replaceAll(/_([a-z])/g, (_, letter) => {
        return letter.toUpperCase();
      }) as keyof UTMData;
      utmData[camelCaseKey] = value;
    }
  }

  return utmData;
}

/**
 * Format UTM data for user creation payload
 * @param utmData - The UTM data to format
 */
export function formatUTMDataForUser(utmData: null | UTMData): {
  campaign?: string;
  landingPage?: string;
  referer?: string;
  source?: string;
} {
  if (!utmData) {
    return {};
  }

  // Check for stored referrer code (survives SSO redirects)
  let storedReferrerCode: null | string = null;
  try {
    storedReferrerCode = sessionStorage.getItem(REFERRER_CODE_KEY);
  }
  catch (error) {
    console.warn('[UTM Tracking] Failed to get stored referrer code:', error);
  }

  // Priority: referrerCode (explicit) > stored referrerCode > document.referrer
  const refererValue = utmData.referrerCode || storedReferrerCode || utmData.referrer || undefined;

  return {
    // Map UTM campaign to the 'campaign' field
    campaign: utmData.utmCampaign || undefined,
    // Map UTM landing page to the 'landingPage' field
    landingPage: utmData.landingPage || undefined,
    // Use referrerCode if present, otherwise fall back to document.referrer
    referer: refererValue,
    // Map UTM source to the 'source' field
    source: utmData.utmSource || undefined,
  };
}

/**
 * Get the current referrer information
 */
export function getReferrerInfo(): string {
  // Use document.referrer if available, otherwise check for stored referrer
  return document.referrer || window.location.origin;
}

/**
 * Retrieve stored UTM data from localStorage
 */
export function getStoredUTMData(): null | UTMData {
  try {
    const storedData = localStorage.getItem(UTM_STORAGE_KEY);
    return storedData ? JSON.parse(storedData) : null;
  }
  catch (error) {
    console.warn('[UTM Tracking] Failed to retrieve UTM data:', error);
    return null;
  }
}

/**
 * Initialize UTM tracking - captures and stores data if not already present
 */
export function initializeUTMTracking(): null | UTMData {
  const currentUTMData = captureUTMData();

  // Check if we have any meaningful UTM data or if this is an external referrer
  const hasUTMParameters = Object.keys(currentUTMData).some((key) => {
    return key.startsWith('utm') && currentUTMData[key as keyof UTMData];
  },
  );
  const hasExternalReferrer = currentUTMData.referrer
    && !currentUTMData.referrer.includes(window.location.hostname);

  // Store data if we have UTM params, external referrer, or if this is the first visit
  if (hasUTMParameters || hasExternalReferrer || !getStoredUTMData()) {
    storeUTMData(currentUTMData);
    return currentUTMData;
  }

  return getStoredUTMData();
}

/**
 * Store UTM data in localStorage (only if not already present)
 * @param utmData - The UTM data to store
 */
export function storeUTMData(utmData: UTMData): void {
  try {
    const existingData = localStorage.getItem(UTM_STORAGE_KEY);

    // Only store if no existing data (first session only)
    if (existingData) {
      return;
    }

    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
  }
  catch (error) {
    console.warn('[UTM Tracking] Failed to store UTM data:', error);
  }
}
